import React, { useEffect, useState } from 'react';
import {
  X, Calendar, User, Tag, CheckSquare, MessageSquare, Paperclip, Archive, Trash2,
  Plus, Send, Pencil, Check, Upload, Loader2, Image as ImageIcon, FileText,
} from 'lucide-react';
import { TaskBoardTask, TaskProject, ProjectChecklistItem, ProjectComment, ProjectAttachment } from '../../types';
import { PRIORITIES, PRIORITY_LABELS, PRIORITY_COLORS } from '../../utils/projectConstants';
import { getProjectColumns, appendActivity } from '../../utils/projectUtils';
import {
  SystemUser,
  formatAssigneeDisplay,
  normalizeAssigneeInput,
} from '../../utils/projectAssignees';
import { formatArgentinaDate } from '../../utils/dateUtils';
import {
  getFileAttachments,
  getImageAttachments,
  removeProjectAttachmentFromStorage,
  safeProjectAttachments,
  uploadProjectAttachment,
} from '../../utils/projectAttachments';
import { v4 as uuidv4 } from 'uuid';

interface ProjectTaskPanelProps {
  task: TaskBoardTask | null;
  project: TaskProject | null;
  systemUsers: SystemUser[];
  currentUserEmail?: string | null;
  onClose: () => void;
  onUpdate: (id: string, updates: Partial<TaskBoardTask>) => Promise<TaskBoardTask | void>;
  onArchive: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const ProjectTaskPanel: React.FC<ProjectTaskPanelProps> = ({
  task,
  project,
  systemUsers,
  currentUserEmail,
  onClose,
  onUpdate,
  onArchive,
  onDelete,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState<TaskBoardTask['priority']>('medium');
  const [assignee, setAssignee] = useState('');
  const [startDate, setStartDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [numberValue, setNumberValue] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [checklist, setChecklist] = useState<ProjectChecklistItem[]>([]);
  const [comments, setComments] = useState<ProjectComment[]>([]);
  const [newCheckItem, setNewCheckItem] = useState('');
  const [newComment, setNewComment] = useState('');
  const [editingCheckId, setEditingCheckId] = useState<string | null>(null);
  const [editingCheckText, setEditingCheckText] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [saving, setSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const columns = getProjectColumns(project);
  const attachments = safeProjectAttachments(task?.attachments);
  const imageAttachments = getImageAttachments(task?.attachments);
  const fileAttachments = getFileAttachments(task?.attachments);

  useEffect(() => {
    if (!task) return;
    setTitle(task.title);
    setDescription(task.description || '');
    setStatus(task.status);
    setPriority(task.priority || 'medium');
    setAssignee(task.assignee ? formatAssigneeDisplay(task.assignee, systemUsers) : '');
    setStartDate(task.start_date || '');
    setDueDate(task.due_date || '');
    setNumberValue(task.number_value != null ? String(task.number_value) : '');
    setTagsInput((task.tags || []).join(', '));
    setChecklist(task.checklist || []);
    setComments(task.comments || []);
    setEditingCheckId(null);
    setEditingCommentId(null);
  }, [task?.id, systemUsers]);

  useEffect(() => {
    if (!task) return;
    setChecklist(task.checklist || []);
    setComments(task.comments || []);
  }, [task?.checklist, task?.comments]);

  if (!task) return null;

  const save = async (patch: Partial<TaskBoardTask>, message?: string) => {
    setSaving(true);
    try {
      const activity = message
        ? appendActivity(task, message, currentUserEmail)
        : task.activity;
      const updated = await onUpdate(task.id, { ...patch, activity });
      if (updated) {
        if ('checklist' in patch) setChecklist(updated.checklist || []);
        if ('comments' in patch) setComments(updated.comments || []);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCore = () => {
    const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean);
    const parsedNumber = numberValue.trim() === '' ? null : Number(numberValue);
    save({
      title: title.trim(),
      description: description.trim() || null,
      status,
      priority,
      assignee: normalizeAssigneeInput(assignee, systemUsers),
      start_date: startDate || null,
      due_date: dueDate || null,
      number_value: parsedNumber !== null && !Number.isNaN(parsedNumber) ? parsedNumber : null,
      tags,
      checklist,
      completed: status.toLowerCase().includes('hecho') || status.toLowerCase().includes('complet'),
    }, 'Actualizó la tarea');
  };

  const toggleCheckItem = (id: string) => {
    const next = checklist.map(i => i.id === id ? { ...i, done: !i.done } : i);
    setChecklist(next);
    save({ checklist: next }, 'Actualizó checklist');
  };

  const addCheckItem = () => {
    const text = newCheckItem.trim();
    if (!text) return;
    const next = [...checklist, { id: uuidv4(), text, done: false }];
    setChecklist(next);
    setNewCheckItem('');
    save({ checklist: next }, 'Agregó item al checklist');
  };

  const startEditCheckItem = (item: ProjectChecklistItem) => {
    setEditingCheckId(item.id);
    setEditingCheckText(item.text);
  };

  const commitEditCheckItem = () => {
    if (!editingCheckId) return;
    const text = editingCheckText.trim();
    if (!text) {
      deleteCheckItem(editingCheckId);
      return;
    }
    const next = checklist.map(i => i.id === editingCheckId ? { ...i, text } : i);
    setChecklist(next);
    setEditingCheckId(null);
    save({ checklist: next }, 'Editó item del checklist');
  };

  const deleteCheckItem = (id: string) => {
    const next = checklist.filter(i => i.id !== id);
    setChecklist(next);
    setEditingCheckId(null);
    save({ checklist: next }, 'Eliminó item del checklist');
  };

  const addAttachments = async (files: File[]) => {
    if (!task || files.length === 0) return;
    setIsUploading(true);
    setUploadError(null);
    try {
      const next: ProjectAttachment[] = [...attachments];
      for (const file of files) {
        const uploaded = await uploadProjectAttachment(task.id, file);
        next.push(uploaded);
      }
      await save({ attachments: next }, `Adjuntó ${files.length} archivo(s)`);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Error al subir archivo';
      setUploadError(message);
    } finally {
      setIsUploading(false);
    }
  };

  const removeAttachment = async (attachment: ProjectAttachment) => {
    if (!task) return;
    const next = attachments.filter(a => a.url !== attachment.url);
    await removeProjectAttachmentFromStorage(attachment.path);
    await save({ attachments: next }, 'Eliminó un adjunto');
  };

  const addComment = async () => {
    const text = newComment.trim();
    if (!text) return;
    const comment: ProjectComment = {
      id: uuidv4(),
      text,
      author: currentUserEmail || null,
      created_at: new Date().toISOString(),
    };
    const next = [...comments, comment];
    setComments(next);
    setNewComment('');
    await save({ comments: next }, 'Agregó un comentario');
  };

  const startEditComment = (c: ProjectComment) => {
    setEditingCommentId(c.id);
    setEditingCommentText(c.text);
  };

  const commitEditComment = async () => {
    if (!editingCommentId) return;
    const text = editingCommentText.trim();
    if (!text) {
      deleteComment(editingCommentId);
      return;
    }
    const next = comments.map(c => c.id === editingCommentId ? { ...c, text } : c);
    setComments(next);
    setEditingCommentId(null);
    await save({ comments: next }, 'Editó un comentario');
  };

  const deleteComment = async (id: string) => {
    const next = comments.filter(c => c.id !== id);
    setComments(next);
    setEditingCommentId(null);
    await save({ comments: next }, 'Eliminó un comentario');
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/20 dark:bg-black/40 z-40" onClick={onClose} />
      <aside className="fixed right-0 top-0 bottom-0 w-full max-w-md z-50 bg-white dark:bg-gray-950 border-l border-gray-200 dark:border-gray-800 shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: PRIORITY_COLORS[(priority || 'medium') as keyof typeof PRIORITY_COLORS] }} />
            <select
              value={status}
              onChange={e => { setStatus(e.target.value); save({ status: e.target.value }, `Movió a ${e.target.value}`); }}
              className="text-xs font-medium bg-transparent border-none outline-none text-gray-600 dark:text-gray-400 cursor-pointer"
            >
              {columns.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => onArchive(task.id)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400" title="Archivar">
              <Archive className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => onDelete(task.id)} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-red-400" title="Eliminar">
              <Trash2 className="w-4 h-4" />
            </button>
            <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            onBlur={handleSaveCore}
            className="w-full text-lg font-semibold bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-400"
            placeholder="Título de la tarea"
          />

          {project && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: project.color || '#6366f1' }} />
              {project.name}
            </div>
          )}

          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1 block">Descripción</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              onBlur={handleSaveCore}
              rows={4}
              placeholder="Agregar descripción..."
              className="w-full text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 py-2 resize-none outline-none focus:ring-1 focus:ring-indigo-400 text-gray-800 dark:text-gray-200"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1 flex items-center gap-1"><User className="w-3 h-3" /> Responsable</label>
              <input
                list="assignee-list"
                value={assignee}
                onChange={e => setAssignee(e.target.value)}
                onBlur={handleSaveCore}
                className="w-full text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 outline-none"
                placeholder="Usuario del sistema o texto libre..."
              />
              <datalist id="assignee-list">
                {systemUsers.map(u => (
                  <option key={u.id} value={u.email}>{u.displayName}</option>
                ))}
              </datalist>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1 block">Prioridad</label>
              <select
                value={priority || 'medium'}
                onChange={e => { setPriority(e.target.value as TaskBoardTask['priority']); save({ priority: e.target.value as TaskBoardTask['priority'] }, 'Cambió prioridad'); }}
                className="w-full text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 outline-none"
              >
                {PRIORITIES.map(p => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Inicio</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} onBlur={handleSaveCore} className="w-full text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 outline-none" />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Límite</label>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} onBlur={handleSaveCore} className="w-full text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 outline-none" />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1 block">Valor numérico</label>
              <input
                type="number"
                step="any"
                value={numberValue}
                onChange={e => setNumberValue(e.target.value)}
                onBlur={handleSaveCore}
                placeholder="Para sumas en columnas"
                className="w-full text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1 flex items-center gap-1"><Tag className="w-3 h-3" /> Tags</label>
            <input
              value={tagsInput}
              onChange={e => setTagsInput(e.target.value)}
              onBlur={handleSaveCore}
              placeholder="Separados por coma"
              className="w-full text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 outline-none"
            />
          </div>

          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2 flex items-center gap-1"><CheckSquare className="w-3 h-3" /> Checklist</label>
            <div className="space-y-1">
              {checklist.map(item => (
                <div key={item.id} className="flex items-center gap-1 group">
                  <input
                    type="checkbox"
                    checked={item.done}
                    onChange={() => toggleCheckItem(item.id)}
                    className="rounded border-gray-300 shrink-0"
                  />
                  {editingCheckId === item.id ? (
                    <input
                      autoFocus
                      value={editingCheckText}
                      onChange={e => setEditingCheckText(e.target.value)}
                      onBlur={commitEditCheckItem}
                      onKeyDown={e => {
                        if (e.key === 'Enter') commitEditCheckItem();
                        if (e.key === 'Escape') setEditingCheckId(null);
                      }}
                      className="flex-1 text-xs rounded border border-indigo-300 dark:border-indigo-600 bg-white dark:bg-gray-900 px-2 py-0.5 outline-none"
                    />
                  ) : (
                    <span
                      className={`flex-1 text-xs cursor-text ${item.done ? 'line-through text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}
                      onDoubleClick={() => startEditCheckItem(item)}
                    >
                      {item.text}
                    </span>
                  )}
                  <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <button
                      type="button"
                      onClick={() => startEditCheckItem(item)}
                      className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400"
                      title="Editar"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteCheckItem(item.id)}
                      className="p-0.5 rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-gray-400 hover:text-red-500"
                      title="Eliminar"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-1 mt-2">
              <input
                value={newCheckItem}
                onChange={e => setNewCheckItem(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addCheckItem(); }}
                placeholder="Nuevo item..."
                className="flex-1 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 outline-none"
              />
              <button type="button" onClick={addCheckItem} className="p-1 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700">
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Comentarios</label>
            <div className="space-y-2 mb-2">
              {comments.map(c => (
                <div key={c.id} className="text-xs bg-gray-50 dark:bg-gray-900 rounded-lg px-3 py-2 group">
                  {editingCommentId === c.id ? (
                    <div className="flex gap-1">
                      <input
                        autoFocus
                        value={editingCommentText}
                        onChange={e => setEditingCommentText(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') commitEditComment();
                          if (e.key === 'Escape') setEditingCommentId(null);
                        }}
                        className="flex-1 text-xs rounded border border-indigo-300 dark:border-indigo-600 bg-white dark:bg-gray-800 px-2 py-1 outline-none"
                      />
                      <button type="button" onClick={commitEditComment} className="p-1 rounded bg-indigo-600 text-white">
                        <Check className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="text-gray-800 dark:text-gray-200">{c.text}</p>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-[10px] text-gray-400">{c.author || 'Usuario'} · {formatArgentinaDate(c.created_at.split('T')[0])}</p>
                        <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            type="button"
                            onClick={() => startEditComment(c)}
                            className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-400"
                            title="Editar"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteComment(c.id)}
                            className="p-0.5 rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-gray-400 hover:text-red-500"
                            title="Eliminar"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-1">
              <input
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addComment(); }}
                placeholder="Escribir comentario..."
                className="flex-1 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 outline-none"
              />
              <button type="button" onClick={addComment} className="p-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-1">
                <Paperclip className="w-3 h-3" /> Archivos
              </label>
              <label className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
                {isUploading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Upload className="w-3 h-3" />
                )}
                Subir
                <input
                  type="file"
                  multiple
                  className="hidden"
                  disabled={isUploading}
                  onChange={async e => {
                    const files = Array.from(e.target.files || []);
                    e.currentTarget.value = '';
                    await addAttachments(files);
                  }}
                />
              </label>
            </div>

            <div
              className="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 p-2.5 bg-gray-50/60 dark:bg-gray-900/40"
              onDragOver={e => e.preventDefault()}
              onDrop={async e => {
                e.preventDefault();
                await addAttachments(Array.from(e.dataTransfer.files || []));
              }}
            >
              <p className="text-[10px] text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                Arrastrá imágenes o archivos acá
              </p>

              {uploadError && (
                <p className="mt-1 text-[10px] text-red-500">{uploadError}</p>
              )}

              {attachments.length === 0 ? (
                <p className="mt-2 text-[10px] text-gray-400">Sin adjuntos</p>
              ) : (
                <>
                  {imageAttachments.length > 0 && (
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {imageAttachments.map(a => (
                        <div
                          key={a.url}
                          className="group relative rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                        >
                          <a href={a.url} target="_blank" rel="noreferrer" className="block">
                            <img
                              src={a.url}
                              alt={a.name || 'Imagen'}
                              className="h-24 w-full object-cover"
                            />
                          </a>
                          <button
                            type="button"
                            onClick={() => removeAttachment(a)}
                            className="absolute right-1 top-1 p-1 rounded-md bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Quitar"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {fileAttachments.length > 0 && (
                    <div className={`space-y-1 ${imageAttachments.length > 0 ? 'mt-2' : 'mt-2'}`}>
                      {fileAttachments.map(a => (
                        <div
                          key={a.url}
                          className="group flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5"
                        >
                          <FileText className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                          <a
                            href={a.url}
                            target="_blank"
                            rel="noreferrer"
                            className="flex-1 min-w-0 text-xs text-indigo-600 hover:underline truncate"
                          >
                            {a.name || 'Archivo'}
                          </a>
                          <button
                            type="button"
                            onClick={() => removeAttachment(a)}
                            className="p-1 rounded text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Quitar"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {(task.activity?.length || 0) > 0 && (
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2 block">Actividad</label>
              <div className="space-y-1">
                {[...(task.activity || [])].reverse().slice(0, 8).map((a, i) => (
                  <p key={i} className="text-[10px] text-gray-500">
                    {a.message} · {formatArgentinaDate(a.created_at.split('T')[0])}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>

        {saving && (
          <div className="px-4 py-2 text-[10px] text-gray-400 border-t border-gray-100 dark:border-gray-800">Guardando...</div>
        )}
      </aside>
    </>
  );
};

export default ProjectTaskPanel;
